import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Heart, Zap, Bell, ShieldCheck, Loader2, MessageSquare, Link2Off, Repeat, Info } from "lucide-react";

const PERKS = [
  { icon: ShieldCheck, text: "Supporter badge on your profile & every report you submit" },
  { icon: Zap, text: "Priority placement in the modder review queue" },
  { icon: Bell, text: "Instant alerts when a player you reported gets banned" },
  { icon: Heart, text: "You keep the community-run anti-cheat wall alive" },
];

export default function Supporter() {
  const { user, openAuth, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [goal, setGoal] = useState(null);
  const [busy, setBusy] = useState("");
  const [discordBusy, setDiscordBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/payments/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/support-goal").then(({ data }) => setGoal(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const d = params.get("discord");
    if (!d) return;
    if (d === "linked") {
      toast.success(params.get("role") === "1" ? "Discord linked — Verified Supporter role granted! 🛡️" : "Discord linked! Your role activates as soon as you're a supporter.");
      checkAuth();
    } else if (d === "error") {
      toast.error("Couldn't link Discord. Please try again.");
    }
    params.delete("discord"); params.delete("role"); setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectDiscord = async () => {
    if (!user) return openAuth();
    setDiscordBusy(true);
    try {
      const redirect_uri = `${window.location.origin}/api/discord/callback`;
      const { data } = await api.get("/discord/login-url", { params: { redirect_uri } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discord linking isn't available right now.");
      setDiscordBusy(false);
    }
  };

  const unlinkDiscord = async () => {
    setDiscordBusy(true);
    try {
      await api.post("/discord/unlink");
      toast.success("Discord unlinked.");
      await checkAuth();
    } catch {
      toast.error("Could not unlink Discord.");
    } finally {
      setDiscordBusy(false);
    }
  };

  const checkout = async (lookup_key) => {
    if (!user) return openAuth();
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Try again.");
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <div className="animate-fade-up text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#FFB800]/30 bg-[#FFB800]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#FFB800]">
          <Crown size={13} /> Back the Community
        </div>
        <h1 className="mt-4 font-heading text-4xl font-black tracking-tight text-white sm:text-5xl">Become a Supporter</h1>
        <p className="mx-auto mt-3 max-w-xl text-[#94A3B8]">2K Case Files is run by real modders, for the community. Supporters keep it running and get perks that help clean up the game faster.</p>
      </div>

      {goal && (
        <div className="animate-fade-up mt-8 rounded-2xl border border-[#00FF87]/30 bg-[#0F1424] p-6" data-testid="support-goal">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <h3 className="font-heading text-lg font-bold text-white">{goal.month} costs goal</h3>
            <span className="font-mono text-sm text-[#94A3B8]"><span className="font-bold text-[#00FF87]">${goal.raised.toFixed(2)}</span> of ${goal.goal}</span>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-[#141B2D]">
            <div className="h-full rounded-full bg-gradient-to-r from-[#00FF87] to-[#00F0FF] transition-all" style={{ width: `${Math.min(100, (goal.raised / goal.goal) * 100)}%` }} />
          </div>
          <p className="mt-2 text-xs text-[#64748B]">{goal.supporters} active supporter{goal.supporters === 1 ? "" : "s"} keeping the wall online this month. Every bit helps cover hosting.</p>
        </div>
      )}

      {user?.is_supporter && (
        <div className="animate-fade-up mt-8 rounded-2xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-6 text-center" data-testid="supporter-active">
          <Crown className="mx-auto text-[#FFB800]" size={32} />
          <h2 className="mt-2 font-heading text-2xl font-bold text-white">You're a Supporter 🛡️</h2>
          <p className="mt-1 text-[#CBD5E1]">Thank you for backing the wall. All perks below are active on your account.</p>
        </div>
      )}

      {user && (
        <div className="animate-fade-up mt-8 rounded-2xl border border-[#5865F2]/40 bg-[#0F1424] p-6" data-testid="discord-link-card">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#5865F2]/15 text-[#5865F2]"><MessageSquare size={20} /></span>
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Verified Supporter role on Discord</h3>
                {user.discord_id ? (
                  <p className="mt-1 text-sm text-[#94A3B8]">
                    Linked as <span className="font-semibold text-white">@{user.discord_username}</span>.{" "}
                    {user.is_supporter
                      ? <span className="text-[#00FF87]">Your Verified Supporter role is active.</span>
                      : <span>Become a supporter below and your role is granted automatically.</span>}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-[#94A3B8]">Link your Discord so we can automatically give you the <span className="font-semibold text-white">Verified Supporter</span> badge in our server.</p>
                )}
              </div>
            </div>
            {user.discord_id ? (
              <Button data-testid="discord-unlink-btn" onClick={unlinkDiscord} disabled={discordBusy} variant="outline" className="shrink-0 border-[#1E293F] bg-transparent text-[#94A3B8] hover:bg-[#141B2D] hover:text-white">
                {discordBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2Off size={15} className="mr-2" />} Unlink
              </Button>
            ) : (
              <Button data-testid="discord-connect-btn" onClick={connectDiscord} disabled={discordBusy} className="shrink-0 bg-[#5865F2] font-bold text-white hover:bg-[#4752c4]">
                {discordBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquare size={15} className="mr-2" />} Connect Discord
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Perks */}
      <div className="animate-fade-up mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {PERKS.map((p) => (
          <div key={p.text} className="flex items-start gap-3 rounded-xl border border-[#1E293F] bg-[#0F1424] p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FFB800]/15 text-[#FFB800]"><p.icon size={18} /></span>
            <p className="text-sm text-[#CBD5E1]">{p.text}</p>
          </div>
        ))}
      </div>

      {!products ? (
        <div className="flex justify-center py-12 text-[#64748B]"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          {/* Plans */}
          <div className="animate-fade-up mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {products.supporter.map((plan) => (
              <div key={plan.lookup_key} className="relative overflow-hidden rounded-2xl border border-[#FFB800]/30 bg-[#0F1424] p-6">
                <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-[#FFB800]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FFB800]"><Repeat size={10} /> Recurring</span>
                <div className="text-sm font-semibold uppercase tracking-wider text-[#FFB800]">{plan.label} Supporter</div>
                <div className="mt-2 flex items-end gap-1">
                  <span className="font-heading text-4xl font-black text-white">${plan.interval === "year" ? Number(plan.amount).toFixed(2) : plan.amount}</span>
                  <span className="mb-1 text-[#94A3B8]">/{plan.interval}</span>
                </div>
                {plan.interval === "year" ? (
                  <p className="mt-1 text-xs font-semibold text-[#00FF87]" data-testid="yearly-savings">Just ${(Number(plan.amount) / 12).toFixed(2)}/mo — save 20% vs monthly</p>
                ) : (
                  <p className="mt-1 text-xs text-[#64748B]">Billed monthly, cancel anytime</p>
                )}
                <ul className="mt-4 space-y-2 text-sm text-[#CBD5E1]">
                  {PERKS.map((p) => <li key={p.text} className="flex items-center gap-2"><Check size={14} className="text-[#00FF87]" /> {p.text.split(" ").slice(0, 4).join(" ")}…</li>)}
                </ul>
                <Button data-testid={`buy-${plan.lookup_key}`} onClick={() => checkout(plan.lookup_key)} disabled={!!busy} className="mt-5 w-full bg-[#FFB800] font-bold text-[#090C15] hover:bg-[#FFB800]/90">
                  {busy === plan.lookup_key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown size={15} className="mr-2" />}
                  Go {plan.label}
                </Button>
              </div>
            ))}
          </div>

          {/* One-time priority boost */}
          {products.boost && (
            <div className="animate-fade-up mt-6 rounded-2xl border border-[#00F0FF]/30 bg-[#0F1424] p-6" data-testid="boost-bundle">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[#00F0FF]"><Zap size={20} /></span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg font-bold text-white">One-Time Priority Boost</h3>
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#00F0FF]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#00F0FF]"><Zap size={10} /> One-Time</span>
                    </div>
                    <p className="mt-1 max-w-md text-sm text-[#94A3B8]"><span className="font-semibold text-white">${products.boost.amount} — single use.</span> Pushes <span className="font-semibold text-white">one</span> of your reports to the top of the modder review queue. Not a subscription — it's used once and it's gone.</p>
                  </div>
                </div>
                <Button data-testid="buy-report_boost" onClick={() => (user ? navigate("/my") : openAuth())} className="shrink-0 bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
                  <Zap size={15} className="mr-2" /> Boost a report
                </Button>
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#FFB800]/25 bg-[#FFB800]/[0.06] px-3 py-2 text-xs leading-relaxed text-[#FCD34D]" data-testid="boost-disclaimer">
                <Info size={14} className="mt-px shrink-0" />
                <span>Boosting a lot? You're better off as a <span className="font-semibold text-white">Supporter ($7/mo)</span> — <span className="font-semibold text-white">every</span> report you submit stays prioritized automatically, so you don't spend $3 each time.</span>
              </div>
            </div>
          )}

          {/* Tip jar */}
          <div className="animate-fade-up mt-8 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6 text-center" data-testid="tip-jar">
            <Heart className="mx-auto text-[#FF2A55]" size={26} />
            <h3 className="mt-2 font-heading text-xl font-bold text-white">Not ready to subscribe? Drop a tip.</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-[#94A3B8]">If you support what we're doing for our mutual favorite game, help us out — even a little goes a long way toward keeping the wall alive. 🏀</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {products.tips.map((t) => (
                <Button key={t.lookup_key} data-testid={`tip-${t.lookup_key}`} onClick={() => checkout(t.lookup_key)} disabled={!!busy} variant="outline" className="border-[#FF2A55]/40 bg-[#FF2A55]/10 text-white hover:bg-[#FF2A55]/20 hover:text-white">
                  {busy === t.lookup_key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}${t.amount}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}
      <p className="mt-6 text-center text-xs text-[#64748B]">
        Payments handled securely by Stripe.
        {products?.test_mode && <span className="ml-1 text-[#64748B]/80">Test mode — use card 4242 4242 4242 4242.</span>}
      </p>
    </div>
  );
}
